import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {trackSubmitAutoFillFeature} from 'molbio/SequenceAnalyticsHelper';
import * as AutoFillFieldActions from 'registry/AutoFillFieldActions';
import {FIELD_TYPES, FieldType} from 'schemas/fields/SchemaFieldConstants';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import throttle from 'util/throttle';

function AutofillInput({
  autofillOptions,
  fileId,
}: {
  autofillOptions: Array<AutofillActions>;
  fileId: string;
}): JSX.Element {
  const handleAutofillAction = (action: AutofillActions): void => {
    trackSubmitAutoFillFeature([fileId], ActionOrigin.ENTITY_PAGE_METADATA_TAB_SCHEMA_TABLE, action.name);
    action.autofillActionFn(fileId);
  };
  const sharedButtonProps: ButtonProps = {
    size: ButtonSize.MINI,
    buttonStyle: ButtonStyle.PRIMARY,
    className: 'u-mini-spacing-left',
    icon: 'info',
  };

  if (autofillOptions.length === 1) {
    const autofillOption = autofillOptions[0];
    return (
      <Button
        {...sharedButtonProps}
        text={`Autofill ${autofillOption.name.toLowerCase()}`}
        onMouseDown={() => {
          handleAutofillAction(autofillOption);
        }}
        dataTestAttrs={{component: 'AutoFillButton', element: `autofill${autofillOption.name}`}}
      />
    );
  } else {
    return (
      <Dropdown
        menu={
          <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.MEDIUM}>
            {autofillOptions.map((autofillOption) => (
              <Option
                key={autofillOption.name}
                onSelect={() => {
                  handleAutofillAction(autofillOption);
                }}
              >
                {autofillOption.name}
              </Option>
            ))}
          </Menu>
        }
      >
        <Button {...sharedButtonProps} text="Autofill" isDropdown={true} />
      </Dropdown>
    );
  }
}

export type AutofillNames = 'Parts' | 'Translations' | 'Transcriptions';

interface AutofillActions {
  name: AutofillNames;
  fieldType: FieldType;
  // Due to throttling this is a nested promise, but we don't actually await on it anyway since autofilling
  // can take upwards of several minutes.
  autofillActionFn: (sequenceId: string) => Promise<Promise<void>>;
}

const THROTTLE_INTERVAL = 1000;

export const AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS: Array<AutofillActions> = [
  {
    name: 'Parts',
    fieldType: FIELD_TYPES.PART_LINK.type,
    autofillActionFn: throttle(AutoFillFieldActions.autoFillPartFields, THROTTLE_INTERVAL),
  },
  {
    name: 'Transcriptions',
    fieldType: FIELD_TYPES.TRANSCRIPTION_LINK.type,
    autofillActionFn: throttle(AutoFillFieldActions.autofillTranscriptions, THROTTLE_INTERVAL),
  },
  {
    name: 'Translations',
    fieldType: FIELD_TYPES.TRANSLATION_LINK.type,
    autofillActionFn: throttle(AutoFillFieldActions.autofillTranslations, THROTTLE_INTERVAL),
  },
];

export interface AutoFillButtonProps {
  isEditing: boolean;
  isOligo: boolean;
  fileId: string;
  areTagsEditable: boolean;
  tagSchema: TagSchema | null;
}

/**
 * Button prompting to run autofill on parts, translations, or transcriptions, as applicable.
 *
 * If none are applicable, this renders to nothing. Otherwise, this is a button dropdown letting the user pick which
 * action to take.
 */
export default function AutoFillButton({
  isEditing,
  isOligo,
  fileId,
  areTagsEditable,
  tagSchema,
}: AutoFillButtonProps): JSX.Element | null {
  // Only render if this is a non-oligo sequence with an editable bioentity link field (part/translation/transcription).
  // Start with some basic checks before checking bioentity links.
  if (!areTagsEditable || !tagSchema || isOligo || isEditing) {
    return null;
  }
  const autofillOptions = AUTOFILL_COMPATIBLE_BIOENTITY_OPTIONS.filter((autofillOption) =>
    tagSchema.fields.some((f) => f.fieldType === autofillOption.fieldType)
  );
  if (autofillOptions.length === 0) {
    return null;
  }
  return (
    <Tooltip text="Apply autofill to visualize schema field links on sequence maps">
      <AutofillInput autofillOptions={autofillOptions} fileId={fileId} />
    </Tooltip>
  );
}
